/**
 * useNotifications Hook
 *
 * Manages fetching and state for user notifications using the new Activity API.
 * Provides unread count, notification list, and mark as seen functionality.
 * Uses React Query for caching and automatic background refetching.
 * Read status is persisted to the backend database via markEventRead API.
 */

import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ActivityEvent } from '@/api/generated/client';


interface UseNotificationsOptions {
  limit?: number;
  pollingInterval?: number; // milliseconds, 0 to disable, minimum 30000ms enforced
  pauseWhenHidden?: boolean; // pause polling when tab is not visible (not used with React Query)
}

const MIN_POLLING_INTERVAL = 30000; // 30 seconds minimum to prevent accidental rapid polling

interface NotificationData {
  events: ActivityEvent[];
  unreadCount: number;
  lastSeenEventId: number;
}

interface UseNotificationsReturn {
  notifications: ActivityEvent[];
  unreadNotifications: ActivityEvent[];
  unreadCount: number;
  lastSeenEventId: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  /** Mark a single notification as read (persisted to backend database) */
  markOneAsRead: (eventId: number) => Promise<void>;
  /** Mark a specific event as seen (advances server cursor to this event) */
  markAsSeen: (eventId: number) => Promise<void>;
  /** Mark all notifications as read (advances server cursor) */
  markAllAsSeen: () => Promise<void>;
  /** Check if a specific event is unread */
  isUnread: (event: ActivityEvent) => boolean;
}

export function useNotifications(options: UseNotificationsOptions = {}): UseNotificationsReturn {
  const {
    limit = 50,
    pollingInterval = 60000,
  } = options;

  const safePollingInterval = pollingInterval > 0 && pollingInterval < MIN_POLLING_INTERVAL
    ? MIN_POLLING_INTERVAL
    : pollingInterval;

  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    error,
    refetch: queryRefetch,
  } = useQuery<NotificationData, Error>({
    queryKey: ['notifications', limit],
    queryFn: async () => {
      const response = await apiClient.activities.getUserNotifications({
        limit,
      });
      return {
        events: response.events || [],
        unreadCount: response.unreadCount,
        lastSeenEventId: response.lastSeenEventId,
      };
    },
    placeholderData: (previousData) => previousData,
    staleTime: 0, // Always consider data stale so invalidateQueries triggers immediate refetch
    refetchInterval: safePollingInterval > 0 ? safePollingInterval : false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  const notifications = useMemo(() => data?.events || [], [data?.events]);
  const lastSeenEventId = data?.lastSeenEventId || 0;

  // Unread notifications are those above the cursor (backend filters by read_at)
  const unreadCount = useMemo(() => {
    return notifications.filter(n => n.id > lastSeenEventId).length;
  }, [notifications, lastSeenEventId]);

  // Memoized list of unread notifications
  const unreadNotifications = useMemo(() => {
    return notifications.filter(n => n.id > lastSeenEventId);
  }, [notifications, lastSeenEventId]);

  const isUnread = useCallback((event: ActivityEvent) => {
    return event.id > lastSeenEventId;
  }, [lastSeenEventId]);

  const refetch = useCallback(async () => {
    await queryRefetch();
  }, [queryRefetch]);

  const markOneAsRead = useCallback(async (eventId: number) => {
    try {
      await apiClient.activities.markEventRead({ eventId });
      // Refetch to get updated read status from backend
      await queryRefetch();
    } catch (err) {
      console.error('Failed to mark event as read:', err);
      throw err;
    }
  }, [queryRefetch]);

  const markAsSeen = useCallback(async (eventId: number) => {
    try {
      const response = await apiClient.activities.markActivitySeen({
        markSeenRequest: {
          lastSeenEventId: eventId,
        },
      });

      // Update the query cache with new cursor position
      queryClient.setQueryData<NotificationData>(['notifications', limit], (old) => {
        if (!old) return old;
        return {
          ...old,
          lastSeenEventId: response.lastSeenEventId,
          unreadCount: old.events.filter(n => n.id > response.lastSeenEventId).length,
        };
      });
    } catch (err) {
      console.error('Failed to mark notification as seen:', err);
      throw err;
    }
  }, [queryClient, limit]);

  const markAllAsSeen = useCallback(async () => {
    if (notifications.length === 0) return;
    const maxEventId = Math.max(...notifications.map(n => n.id));
    await markAsSeen(maxEventId);
  }, [notifications, markAsSeen]);

  return {
    notifications,
    unreadNotifications,
    unreadCount,
    lastSeenEventId,
    isLoading,
    error: error || null,
    refetch,
    markOneAsRead,
    markAsSeen,
    markAllAsSeen,
    isUnread,
  };
}
